import { useEffect, useRef, useState } from 'react';
import api from '../api';

const elapsedAtReceipt = (start, server) => {
    const elapsed = Date.parse(server) - Date.parse(start);
    return Number.isFinite(elapsed) ? Math.max(0, elapsed) : null;
};

export default function useCallDuration(tokens, onDurationChange, connectionState) {
    const refreshRef = useRef(null);
    const { session_started_at: start, server_time: server,
        session_clock_url: url, clock_received_at: received } = tokens || {};
    const [duration, setDuration] = useState(() => Math.floor((elapsedAtReceipt(start, server) || 0)
        / 1000 + (Number.isFinite(received) ? Math.max(0, performance.now() - received) / 1000 : 0)));

    useEffect(() => {
        let active = true;
        let pending = false;
        const controller = new AbortController();
        let base = elapsedAtReceipt(start, server) || 0;
        let receipt = Number.isFinite(received) ? received : performance.now();
        const update = () => {
            if (active) setDuration(Math.floor(Math.max(0, base + performance.now() - receipt) / 1000));
        };
        const refresh = async () => {
            if (!url || pending || !active || document.visibilityState === 'hidden') return;
            pending = true;
            try {
                const response = await api.get(url, { signal: controller.signal, timeout: 10000, skipErrorToast: true });
                const snapshot = response?.data || response;
                const elapsed = elapsedAtReceipt(snapshot?.session_started_at, snapshot?.server_time);
                if (active && elapsed !== null) {
                    base = elapsed;
                    receipt = performance.now();
                    update();
                }
            } catch {
                // Offline/reconnecting: retain the last server anchor and retry later.
            } finally {
                pending = false;
            }
        };
        const resume = () => { update(); refresh(); };
        refreshRef.current = refresh;
        update();
        const ticker = setInterval(update, 1000);
        const sync = url ? setInterval(refresh, 30000) : null;
        document.addEventListener('visibilitychange', resume);
        window.addEventListener('focus', resume);
        window.addEventListener('online', resume);
        return () => {
            active = false;
            refreshRef.current = null;
            controller.abort();
            clearInterval(ticker);
            if (sync !== null) clearInterval(sync);
            document.removeEventListener('visibilitychange', resume);
            window.removeEventListener('focus', resume);
            window.removeEventListener('online', resume);
        };
    }, [start, server, url, received]);

    useEffect(() => {
        if (connectionState?.toLowerCase() === 'connected') refreshRef.current?.();
    }, [connectionState]);

    useEffect(() => { onDurationChange?.(duration); }, [duration, onDurationChange]);
    return duration;
}
